/**
 * AppToaster — mounts the @ui/Toaster viewport once and bridges two
 * app-specific toast sources into it:
 *
 *   1. Inertia `flash` (after every successful navigation):
 *        - flash.notice → toast.success(message)
 *        - flash.alert  → toast.error(message)
 *
 *   2. Custom `app:toast` window events (fired from anywhere in the app):
 *        window.dispatchEvent(new CustomEvent("app:toast", {
 *          detail: { type: "notice" | "alert", message: "…" }
 *        }))
 *
 * Mount once near the layout root.
 */
import { useEffect } from "react"
import { router } from "@inertiajs/react"
import { Toaster, toast } from "@ui/Toast"

type FlashPayload = { notice?: string; alert?: string } | null | undefined

function emit(type: string, message: string) {
  if (!message) return
  if (type === "notice") {
    toast.success(message)
  } else {
    toast.error(message)
  }
}

export default function AppToaster() {
  useEffect(() => {
    const remove = router.on("success", (event) => {
      const flash = (event.detail.page.props as { flash?: FlashPayload }).flash
      if (flash?.notice) emit("notice", flash.notice)
      if (flash?.alert) emit("alert", flash.alert)
    })
    return remove
  }, [])

  useEffect(() => {
    function handle(e: Event) {
      const detail = (e as CustomEvent<{ type?: string; message?: string }>).detail
      if (!detail?.message) return
      emit(detail.type ?? "notice", detail.message)
    }
    window.addEventListener("app:toast", handle)
    return () => window.removeEventListener("app:toast", handle)
  }, [])

  return <Toaster />
}
